/* eslint-disable spire/export-styles */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    className = "className",
    backgroundColor = "backgroundColor",
    padding = "padding",
    content = "content",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.backgroundColor]: string;
        [fields.padding]: number;
        [fields.content]: string;
        [fields.customCSS]: string;
    };
}

export interface RichContentStyles {
    wrapper?: InjectableCss;
}

const richContentStyles = {
    wrapper: {},
};

const defaultCustomCss = `.wrapper{
}`;

export const RichContent: React.FunctionComponent<Props> = props => {
    if (!props.fields.content) {
        return null;
    }

    const styles = useMergeStyles("richContent", richContentStyles);

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}

            &#RichContext-box h1,strong,a > span , a[class^='StyledButton'] > span[class^='TypographyStyle'] {
                font-family: "OpenSansBold" !important;
            }

            &#RichContext-box .home-head h1 {
                font-family: "NeuronBold" !important;
            }

            &#RichContext-box {
                a[class^="StyledButton"] > span[class^="TypographyStyle"] {
                    //font-family: "OpenSans" !important;
                    font-size: 14px;
                }
            }

            a#registerForClass {
                margin-top: 0 !important;
                :hover span {
                    text-decoration: underline !important;
                }
            }

            &#RichContext-box li {
                font-size: 14px;
            }

            @media only screen and (min-width: 320px) and (max-width: 767px) and (orientation: portrait) {
                .pr-15,
                .pl-15,
                .mr-30,
                .left-space,
                .right-space {
                    padding: 0;
                    margin: 0;
                }
                &#RichContext-box .promo-box {
                    margin-bottom: 15px;
                }
            }

            @media only screen and (max-width: 767px) {
                #pageIntro {
                    padding: 30px 0 0;
                }
                #pageIntro p {
                    font-size: 14px;
                    padding: 0 15px;
                }
                #pageIntro h2 {
                    margin: 0 15px;
                }
                .video-content.service-video {
                    height: 100%;
                    width: 100%;
                }
                .video-content.service-video-large {
                    width: 100%;
                    height: 100%;
                    max-width: 100%;
                }

                &#RichContext-box h2 {
                    font-size: 22px;
                    line-height: 24px;
                    margin-top: 10px;
                }

                &#RichContext-box {
                    span[class^="LazyImageStyle"] {
                        max-width: 100%;
                        justify-content: flex-start;
                        height: 100%;
                    }
                }

                .video-content.brand-video {
                    height: 190px;
                }
                &#RichContext-box .sub-block .area_sub_head ul {
                    margin: 0px;
                }
                &#RichContext-box .sub-category-block .area_sub_head ul {
                    margin: 0px;
                }

                &#RichContext-box .img-dec .content {
                    width: 100%;
                }
                &#RichContext-box .cat-sub-section-one {
                    width: 100%;
                    padding-right: 0px;
                }
                &#RichContext-box .cat-sub-section-two {
                    width: 100%;
                }
                &#RichContext-box .cat-section-video-wrapper,
                .cat-section-video-wrappers {
                    height: 100%;
                    width: 100%;
                }
                &#RichContext-box .plr {
                    padding: 0 15px;
                }
                &#RichContext-box .video-modal {
                    width: 100%;
                    height: 100%;
                }
                &#RichContext-box .img-dec img {
                    width: 100%;
                }
            }

            @media (max-width: 600px) {
                .promo-1-img {
                    height: 320px !important;
                }
            }
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper} id="RichContext-box">
            <ContentWrapper
                className={`wrapper ${props.fields.className}`}
                {...styles.wrapper}
                backgroundColor={props.fields.backgroundColor}
                padding={props.fields.padding}
            >
                {parse(props.fields.content, parserOptions)}
            </ContentWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: RichContent,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            {
                name: fields.className,
                displayName: "ClassName",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.padding,
                editorTemplate: "IntegerField",
                displayName: "Content Padding",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<{ backgroundColor: string; padding: number } & InjectableCss>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ padding }) => (padding ? `padding: ${padding}px;` : null)};
    & > *:last-child {
        margin: 0;
    }
    ${injectCss}
`;
